import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, Subscription, take } from 'rxjs';
import { GroupService } from '../../../core/services/group.service';

interface BreadcrumbItem {
  title: string;
  url: string;
  isActive: boolean;
  isClickable: boolean; // Nova propriedade para controlar se o item é clicável
}

@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class PageHeaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() title!: string;
  @Input() title1!: string;
  @Input() title2!: string;
  @Input() activeTitle!: string;

  breadcrumbs: BreadcrumbItem[] = [];
  private routerSubscription: Subscription;
  private processIdCache: Map<string, string> = new Map();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService
  ) {
    // Escuta mudanças na rota (incluindo query params)
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.generateBreadcrumbs();
    });
    
    // Também escuta mudanças nos query params especificamente
    this.route.queryParams.subscribe(() => {
      this.generateBreadcrumbs();
    });
  }

  ngOnInit(): void {
    this.generateBreadcrumbs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['title'] || changes['title1'] || changes['title2'] || changes['activeTitle']) {
      this.generateBreadcrumbs();
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private generateBreadcrumbs(): void {
    const currentUrl = this.router.url;
    // Remove query string antes de fazer split para evitar que fique no último segmento
    const urlWithoutQuery = currentUrl.split('?')[0];
    const segments = urlWithoutQuery.split('/').filter(segment => segment.length > 0);
    const queryParams = this.route.snapshot.queryParams;
    
    this.breadcrumbs = [];
    
    // Navegação para introdução: protocolo > introdução > processo > grupo (se disponível)
    if (currentUrl.includes('/protocol/intro')) {
      // Sempre começa com Protocolo
      this.breadcrumbs.push({
        title: 'Protocolo',
        url: '/protocol/cover',
        isActive: false,
        isClickable: true
      });

      // Adiciona Introdução
      this.breadcrumbs.push({
        title: 'Introdução',
        url: currentUrl.split('?')[0], // Remove query params da URL do breadcrumb
        isActive: !queryParams['process_name'] && !queryParams['group_name'], // Ativo apenas se não houver processo ou grupo
        isClickable: false
      });
      
      // Tenta obter o nome do processo dos query params para exibir no breadcrumb
      const processName = queryParams['process_name'] as string;
      const groupName = queryParams['group_name'] as string;
      
      // Adiciona nome do processo se disponível
      if (processName) {
        this.breadcrumbs.push({
          title: processName,
          url: currentUrl.split('?')[0], // Remove query params da URL do breadcrumb
          isActive: !groupName, // Ativo apenas se não houver grupo
          isClickable: false
        });
      }
      
      // Adiciona nome do grupo se disponível
      if (groupName) {
        this.breadcrumbs.push({
          title: groupName,
          url: currentUrl,
          isActive: true,
          isClickable: false
        });
      }
    }
    // Navegação para questionários: protocolo > questionários > processo > questionário/grupo específico
    else if (currentUrl.includes('/protocol/questionnaires')) {
       // Sempre começa com Protocolo
       this.breadcrumbs.push({
         title: 'Protocolo',
         url: '/protocol',
         isActive: false,
         isClickable: true
       });

       // Verifica se estamos em uma rota g2, g3, g4, g5, g6
       const isG2ToG6Route = currentUrl.includes('/g2') || 
                            currentUrl.includes('/g3') || 
                            currentUrl.includes('/g4') || 
                            currentUrl.includes('/g5') ||
                            currentUrl.includes('/g6');

       // Tenta obter o nome do processo e grupo dos query params
       const processName = queryParams['process_name'] as string;
       const groupName = queryParams['group_name'] as string;
       const isG1Route = currentUrl.includes('/q1') || currentUrl.includes('/q2') || currentUrl.includes('/q3');
       // Verifica se está no hub (apenas /protocol/questionnaires sem paths adicionais)
       const isHub = currentUrl.split('?')[0] === '/protocol/questionnaires' && !isG1Route && !isG2ToG6Route;
       
       // Adiciona Questionários - preserva query params para process_id/group_id (necessários para compartilhamento)
       let questionnairesUrl = '/protocol/questionnaires';
       if (queryParams['process_id'] || queryParams['group_id']) {
         const params = new URLSearchParams(queryParams as Record<string, string>).toString();
         questionnairesUrl = `/protocol/questionnaires?${params}`;
       }
       this.breadcrumbs.push({
         title: 'Questionários',
         url: questionnairesUrl,
         isActive: isHub && !processName && !groupName,
         isClickable: !isG2ToG6Route
       });

       // Adiciona nome do processo se disponível (antes do questionário/grupo específico)
       // Para Grupo 1: mostra processo no hub e nos questionários específicos
       // Para Grupos 2-6: mostra processo antes do grupo
       if (processName && (isG2ToG6Route || isG1Route || isHub)) {
         this.breadcrumbs.push({
           title: processName,
           url: currentUrl.split('?')[0], // Remove query params da URL do breadcrumb
           isActive: isHub && !groupName, // Ativo apenas se estiver no hub sem grupo
           isClickable: false
         });
       }

       // Adiciona nome do grupo se disponível (no hub ou nas rotas do Grupo 1)
       if (groupName && (isHub || isG1Route)) {
         this.breadcrumbs.push({
           title: groupName,
           url: isHub ? currentUrl : currentUrl.split('?')[0], // URL completa no hub, sem query params nas rotas específicas
           isActive: isHub, // Ativo apenas se estiver no hub
           isClickable: false
         });
       }

       // Se estamos em um questionário específico do Grupo 1
       if (isG1Route) {
         let questionnaireType = '';
         if (currentUrl.includes('/q1')) questionnaireType = 'q1';
         else if (currentUrl.includes('/q2')) questionnaireType = 'q2';
         else if (currentUrl.includes('/q3')) questionnaireType = 'q3';
         
         // Define os títulos específicos para cada questionário
         const questionnaireTitles = {
           'q1': 'Questionário 1',
           'q2': 'Questionário 2', 
           'q3': 'Questionário 3'
         };
         
         const questionnaireTitle = questionnaireTitles[questionnaireType as keyof typeof questionnaireTitles] || 'Questionário';
         
         this.breadcrumbs.push({
           title: questionnaireTitle,
           url: currentUrl,
           isActive: true, // Último item nas rotas de questionário específico
           isClickable: false
         });
       } else if (isG2ToG6Route) {
         // Adiciona o título específico para g2-g6
         let groupTitle = '';
         if (currentUrl.includes('/g2')) groupTitle = 'Grupo 2';
         else if (currentUrl.includes('/g3')) groupTitle = 'Grupo 3';
         else if (currentUrl.includes('/g4')) groupTitle = 'Grupo 4';
         else if (currentUrl.includes('/g5')) groupTitle = 'Grupo 5';
         else if (currentUrl.includes('/g6')) groupTitle = 'Grupo 6';
         
         this.breadcrumbs.push({
           title: groupTitle,
           url: currentUrl,
           isActive: true,
           isClickable: false
         });
       }
     }
    
    // Navegação para cartas: protocolo > cartas > carta específica
    else if (currentUrl.includes('/protocol/letters')) {
      // Sempre começa com Protocolo
      this.breadcrumbs.push({
        title: 'Protocolo',
        url: '/protocol',
        isActive: false,
        isClickable: true
      });

      // Adiciona Cartas - clicável apenas se não estiver em uma carta específica
      this.breadcrumbs.push({
        title: 'Cartas',
        url: '/protocol/letters',
        isActive: segments.length === 2, // Ativo apenas se estiver no hub (/protocol/letters)
        isClickable: !currentUrl.includes('/g2') && !currentUrl.includes('/g3') && 
                    !currentUrl.includes('/g4') && !currentUrl.includes('/g5') && 
                    !currentUrl.includes('/g6') // Não clicável nas rotas específicas
      });

      // Se estamos em uma carta específica (g2, g3, g4, g5, g6)
      if (currentUrl.includes('/g2') || currentUrl.includes('/g3') || 
          currentUrl.includes('/g4') || currentUrl.includes('/g5') || 
          currentUrl.includes('/g6')) {
        
        let groupTitle = '';
        if (currentUrl.includes('/g2')) groupTitle = 'Grupo 2';
        else if (currentUrl.includes('/g3')) groupTitle = 'Grupo 3';
        else if (currentUrl.includes('/g4')) groupTitle = 'Grupo 4';
        else if (currentUrl.includes('/g5')) groupTitle = 'Grupo 5';
        else if (currentUrl.includes('/g6')) groupTitle = 'Grupo 6';
        
        this.breadcrumbs.push({
          title: groupTitle,
          url: currentUrl,
          isActive: true,
          isClickable: false
        });
      }
    }
    
    // Navegação linear específica: processos > grupos > users
    else if (segments.includes('admin')) {
      // Sempre começa com Dashboard
      this.breadcrumbs.push({
        title: 'Dashboard',
        url: '/admin',
        isActive: false,
        isClickable: true
      });

      // Se estamos em processos
      if (segments.includes('processes')) {
        const processSegment = segments[2] ?? 'list';
        const processListRoutes: Record<string, { label: string; url: string }> = {
          list: { label: 'Todos', url: '/admin/processes/list' },
          'em-andamento': { label: 'Em andamento', url: '/admin/processes/em-andamento' },
          'concluidos-fechados': { label: 'Concluídos/Fechados', url: '/admin/processes/concluidos-fechados' },
        };
        const isProcessListRoute = processListRoutes[processSegment] !== undefined;

        this.breadcrumbs.push({
          title: 'Processos',
          url: '/admin/processes/list',
          isActive: isProcessListRoute && processSegment === 'list',
          isClickable: !(isProcessListRoute && processSegment === 'list')
        });

        if (isProcessListRoute && processSegment !== 'list') {
          const subRoute = processListRoutes[processSegment];
          this.breadcrumbs.push({
            title: subRoute.label,
            url: subRoute.url,
            isActive: true,
            isClickable: false
          });
        }
      }
      
      // Se estamos em grupos
      if (segments.includes('groups')) {
        const isListRoute = segments.length === 3 && segments[2] === 'list';
        this.breadcrumbs.push({
          title: 'Processos',
          url: '/admin/processes/list',
          isActive: false,
          isClickable: true
        });
        
        this.breadcrumbs.push({
          title: 'Grupos',
          url: currentUrl.split('?')[0], // Mantém query params se houver
          isActive: isListRoute,
          isClickable: !isListRoute
        });
      }
      
      // Se estamos em users
      if (segments.includes('users')) {
        const isListRoute = segments.length === 3 && segments[2] === 'list';
        this.breadcrumbs.push({
          title: 'Administradores',
          url: '/admin/users/list',
          isActive: isListRoute,
          isClickable: !isListRoute
        });
      }
      
      // Se estamos em collaborators
      if (segments.includes('collaborators')) {
        const collaboratorSegment = segments[2] ?? 'list';
        const collaboratorRoutes: Record<string, { label: string; url: string }> = {
          list: { label: 'Lista', url: '/admin/collaborators/list' },
          'colaboradores-permissoes': { label: 'Permissões de colaboradores', url: '/admin/collaborators/colaboradores-permissoes' },
        };
        const isCollaboratorListRoute = collaboratorRoutes[collaboratorSegment] !== undefined;
        const isPermissoesDetailRoute =
          segments.length === 4 &&
          segments[1] === 'collaborators' &&
          segments[2] === 'colaboradores-permissoes';

        this.breadcrumbs.push({
          title: 'Colaboradores',
          url: '/admin/collaborators/list',
          isActive: isCollaboratorListRoute && collaboratorSegment === 'list',
          isClickable: !(isCollaboratorListRoute && collaboratorSegment === 'list')
        });

        if (isCollaboratorListRoute && collaboratorSegment !== 'list') {
          const subRoute = collaboratorRoutes[collaboratorSegment];
          this.breadcrumbs.push({
            title: subRoute.label,
            url: subRoute.url,
            isActive: !isPermissoesDetailRoute,
            isClickable: isPermissoesDetailRoute
          });
        }

        if (isPermissoesDetailRoute) {
          this.breadcrumbs.push({
            title: this.title2 || this.title || 'Processo',
            url: currentUrl.split('?')[0],
            isActive: true,
            isClickable: false
          });
        }

        // Se estamos criando um colaborador
        if (segments.length === 3 && segments[1] === 'collaborators' && segments[2] === 'create') {
          this.breadcrumbs.push({
            title: 'Criar',
            url: '/admin/collaborators/create',
            isActive: true,
            isClickable: false
          });
        }
        // Se estamos editando um colaborador
        else if (segments.length === 4 && segments[1] === 'collaborators' && segments[3] === 'edit') {
          this.breadcrumbs.push({
            title: 'Editar',
            url: currentUrl.split('?')[0],
            isActive: true,
            isClickable: false
          });
        }
      }
      
      // Se estamos em informers
      if (segments.includes('informers')) {
        // Se estamos na lista de informadores com group_id (verificar PRIMEIRO)
        if (
          segments.length >= 3 && 
          segments[0] === 'admin' && 
          segments[1] === 'informers' && 
          segments[2] === 'list' && 
          queryParams['group_id']
        ) {
          const groupId = queryParams['group_id'] as string;
          let processId = queryParams['process_id'] as string;
          
          // Se não tem process_id nos query params, busca do cache ou do grupo
          if (!processId) {
            // Verifica se já temos no cache
            if (this.processIdCache.has(groupId)) {
              processId = this.processIdCache.get(groupId)!;
            } else {
              // Busca do grupo
              this.groupService.getGroupById(groupId)
                .pipe(take(1))
                .subscribe({
                  next: (response) => {
                    if (response.success && response.data?.process?.id) {
                      const fetchedProcessId = response.data.process.id;
                      this.processIdCache.set(groupId, fetchedProcessId);
                      // Atualiza o breadcrumb com o process_id correto
                      this.updateGroupsBreadcrumbUrl(fetchedProcessId);
                    }
                  },
                  error: (error) => {
                    console.error('Erro ao buscar process_id do grupo:', error);
                  }
                });
            }
          }
          
          // Adiciona "Processos" como intermediário
          this.breadcrumbs.push({
            title: 'Processos',
            url: '/admin/processes/list',
            isActive: false,
            isClickable: true
          });
          
          // Adiciona "Grupos" como intermediário com process_id
          const groupsUrl = processId 
            ? `/admin/groups/list?process_id=${processId}&page=1&per_page=12`
            : '/admin/groups/list?page=1&per_page=12';
          this.breadcrumbs.push({
            title: 'Grupos',
            url: groupsUrl,
            isActive: false,
            isClickable: true
          });
          
          // Adiciona "Informadores" como último item (ativo)
          this.breadcrumbs.push({
            title: 'Informadores',
            url: currentUrl,
            isActive: true,
            isClickable: false
          });
        }
        // Se estamos na visualização de questionário (view/group/.../questionnaire/...)
        else if (segments.includes('view') && segments.includes('group') && segments.includes('questionnaire')) {
          const groupIdIndex = segments.indexOf('group');
          const groupId = groupIdIndex !== -1 && segments[groupIdIndex + 1] ? segments[groupIdIndex + 1] : null;
          const processId = queryParams['process_id'];
          
          // Adiciona "Processos" como intermediário
          this.breadcrumbs.push({
            title: 'Processos',
            url: '/admin/processes/list',
            isActive: false,
            isClickable: true
          });
          
          // Adiciona "Grupos" como intermediário com process_id
          const groupsUrl = processId 
            ? `/admin/groups/list?process_id=${processId}&page=1&per_page=12`
            : '/admin/groups/list?page=1&per_page=12';
          this.breadcrumbs.push({
            title: 'Grupos',
            url: groupsUrl,
            isActive: false,
            isClickable: true
          });
          
          // Adiciona "Informadores" como intermediário com group_id
          const informersUrl = groupId 
            ? `/admin/informers/list?group_id=${groupId}`
            : '/admin/informers/list';
          this.breadcrumbs.push({
            title: 'Informadores',
            url: informersUrl,
            isActive: false,
            isClickable: true
          });
          
          // Adiciona "Carta" como último item
          this.breadcrumbs.push({
            title: 'Carta',
            url: currentUrl.split('?')[0],
            isActive: true,
            isClickable: false
          });
        }
        else {
          const informerSegment = segments[2] ?? 'list';
          const informerListRoutes: Record<string, { label: string; url: string }> = {
            list: { label: 'Todos', url: '/admin/informers/list' },
            cardeais: { label: 'Cardeais', url: '/admin/informers/cardeais' },
            dom: { label: 'Dom', url: '/admin/informers/dom' },
            padres: { label: 'Padres', url: '/admin/informers/padres' },
            leigos: { label: 'Leigos', url: '/admin/informers/leigos' },
          };
          const isInformerListRoute = informerListRoutes[informerSegment] !== undefined;

          this.breadcrumbs.push({
            title: 'Informadores',
            url: '/admin/informers/list',
            isActive: isInformerListRoute && informerSegment === 'list',
            isClickable: !(isInformerListRoute && informerSegment === 'list')
          });

          if (isInformerListRoute && informerSegment !== 'list') {
            const subRoute = informerListRoutes[informerSegment];
            this.breadcrumbs.push({
              title: subRoute.label,
              url: subRoute.url,
              isActive: true,
              isClickable: false
            });
          }
        }
      }
      
      // Se estamos em settings
      if (segments.includes('settings')) {
        this.breadcrumbs.push({
          title: 'Configurações',
          url: '/admin/settings',
          isActive: segments.length === 2, // Ativo apenas se estiver em /admin/settings
          isClickable: true
        });
        
        // Se estamos em uma sub-rota de configurações
        if (segments.length > 2) {
          const settingsSubRoute = segments[2];
          let subRouteTitle = '';
          
          // Mapear as sub-rotas para títulos amigáveis
          if (settingsSubRoute === 'dioceses') {
            subRouteTitle = 'Dioceses';
          } else if (settingsSubRoute === 'treatment-pronouns') {
            subRouteTitle = 'Pronomes de Tratamento';
          } else if (settingsSubRoute === 'functions') {
            subRouteTitle = 'Funções';
          } else {
            // Capitalizar primeira letra se não estiver mapeado
            subRouteTitle = settingsSubRoute.charAt(0).toUpperCase() + settingsSubRoute.slice(1).replace(/-/g, ' ');
          }
          
          this.breadcrumbs.push({
            title: subRouteTitle,
            url: `/admin/settings/${settingsSubRoute}`,
            isActive: true,
            isClickable: false
          });
        }
      }
    }
  }

  goToBreadcrumb(url: string): void {
    this.router.navigateByUrl(url);
  }

  private updateGroupsBreadcrumbUrl(processId: string): void {
    // Encontra o breadcrumb de Grupos e atualiza a URL
    const groupsBreadcrumb = this.breadcrumbs.find(b => b.title === 'Grupos');
    if (groupsBreadcrumb) {
      groupsBreadcrumb.url = `/admin/groups/list?process_id=${processId}&page=1&per_page=12`;
      // Força a detecção de mudanças recriando o array
      this.breadcrumbs = [...this.breadcrumbs];
    }
  }

  goToParent(): void {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/').filter(segment => segment.length > 0);
    
    // Se estamos na raiz, vai para home
    if (segments.length === 0) {
      this.router.navigateByUrl('/');
      return;
    }
    
    // Lógica específica para operações CRUD
    // Se estamos em create ou edit, vamos para a lista
    if (segments.length >= 3) {
      const lastSegment = segments[segments.length - 1];
      const secondLastSegment = segments[segments.length - 2];
      
      // Casos como /admin/processes/create -> /admin/processes
      if (lastSegment === 'create') {
        const parentSegments = segments.slice(0, -1);
        const parentUrl = `/${parentSegments.join('/')}`;
        this.router.navigateByUrl(parentUrl);
        return;
      }
      
      // Casos como /admin/processes/edit/id -> /admin/processes  
      if (secondLastSegment === 'edit') {
        const parentSegments = segments.slice(0, -2);
        const parentUrl = `/${parentSegments.join('/')}`;
        this.router.navigateByUrl(parentUrl);
        return;
      }
    }
    
    // Para outros casos, remove apenas o último segmento
    const parentSegments = segments.slice(0, -1);
    const parentUrl = parentSegments.length > 0 ? `/${parentSegments.join('/')}` : '/';
    
    this.router.navigateByUrl(parentUrl);
  }
}
